import { Phone, MapPin, Clock, MessageSquare } from "lucide-react";
import { CONTACT_PAGE } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";

const PHONE_DISPLAY = "(705) 796-4025";
const PHONE_HREF = "tel:+17057964025";
const SMS_HREF = "sms:+17057964025";

export default function Contact() {
  useSeo({
    title: "Drywall Repair in Wasaga Beach — Contact · Southern Georgian Bay Repair Expert · Call (705) 796-4025",
    description:
      "Drywall Repair in Wasaga Beach — call Mike at (705) 796-4025. Southern Georgian Bay Repair Expert serving Wasaga Beach, Collingwood, Stayner, Barrie, Midland and Bluewater Beach.",
    path: "/contact",
  });
  return (
    <div data-testid={CONTACT_PAGE.root}>
      {/* HERO */}
      <section className="pt-12 pb-16 relative overflow-hidden">
        <div className="absolute -top-20 right-0 w-[460px] h-[460px] rounded-full bg-[var(--mustard)] opacity-40 blur-3xl"/>
        <div className="container-x relative">
          <span className="chip">CONTACT</span>
          <h1 className="display-hero mt-5">
            Real People.<br/>
            Real Answers.<br/>
            <span className="text-[var(--coral)]">Right now.</span>
          </h1>
          <p className="mt-6 text-xl text-[var(--ink-soft)] max-w-2xl">
            Connecting you to the help you need, no endless waiting — just genuine support to move you forward, exactly when you need it.
          </p>
          <p className="mt-4 text-base text-[var(--ink)] font-semibold italic max-w-2xl">
            Speak directly to Mike.
          </p>
        </div>
      </section>

      {/* BIG CALL CARD */}
      <section className="pb-20">
        <div className="container-x">
          <div className="rounded-[40px] overflow-hidden bg-[var(--coral)] text-[var(--cream)] p-10 sm:p-16 relative">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--mustard)] opacity-80"/>
            <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[var(--teal)] opacity-80"/>

            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <h2 className="display-xl text-[var(--cream)]">
                  Flawless patches,<br/>
                  <span className="text-[var(--ink)]">invisible repairs,</span><br/>
                  quality you can count on.
                </h2>
                <p className="mt-6 text-[#E8E1D2] text-lg max-w-xl">
                  We take pride in honest, reliable craftsmanship that restores your walls and your peace of mind — satisfaction guaranteed.
                </p>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-4">
                <a
                  href={PHONE_HREF}
                  data-testid={CONTACT_PAGE.submit}
                  className="group bg-[var(--ink)] text-[var(--cream)] rounded-3xl p-8 flex items-center gap-5 hover:bg-[#000] transition-colors"
                >
                  <div className="w-16 h-16 rounded-full bg-[var(--coral)] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <Phone size={28}/>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[#E8E1D2]/60">Tap to call</div>
                    <div className="font-display font-bold text-3xl leading-tight mt-1">{PHONE_DISPLAY}</div>
                  </div>
                </a>
                <a
                  href={SMS_HREF}
                  className="group bg-[var(--cream)] text-[var(--ink)] rounded-3xl p-6 flex items-center gap-4 hover:bg-[var(--mustard)] transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center shrink-0">
                    <MessageSquare size={20}/>
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[var(--muted)]">Prefer to text?</div>
                    <div className="font-display font-bold text-xl mt-0.5">Send a quick SMS</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INFO ROW */}
      <section className="pb-24">
        <div className="container-x grid md:grid-cols-2 gap-5">
          <InfoCard
            icon={<MapPin size={20}/>}
            label="Service area"
            value="Southern Georgian Bay"
            sub="Proudly serving Collingwood to Bluewater Beach — local expert repair."
            bg="var(--ink)"
            text="var(--cream)"
            iconBg="var(--coral)"
          />
          <InfoCard
            icon={<Clock size={20}/>}
            label="Hours"
            value="Mon – Sat"
            sub="7:00am – 7:00pm"
            bg="var(--ink)"
            text="var(--cream)"
            iconBg="var(--coral)"
          />
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon, label, value, sub, bg, text, iconBg, iconText, href }) {
  const inner = (
    <div className="rounded-3xl p-8 h-full flex flex-col" style={{ background: bg, color: text }}>
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
        style={{ background: iconBg, color: iconText || text }}
      >
        {icon}
      </div>
      <div className="text-[0.65rem] uppercase tracking-[0.18em] opacity-70 font-bold">{label}</div>
      <div className="font-display font-bold text-2xl mt-2 break-words">{value}</div>
      {sub && <div className="text-sm opacity-75 mt-2">{sub}</div>}
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-95 transition-opacity">{inner}</a> : inner;
}
